import { z } from 'zod';
import { optionRefSchema } from './dashboard.js';
import { geoFilterShape } from './geography.js';
import { dateRangeQuerySchema, paginationQuerySchema, queryBooleanSchema } from './pagination.js';
import { VISIT_STATUSES } from './visits.js';
/**
 * Assigned visits: a manager plans a visit (branch, projects, instruction, date,
 * priority) for an agent of their team; the agent accepts it and starts the
 * normal visit screen from it, pre-filled. The visit it produces completes it.
 *
 * Assignments are additive: a visit never needs one, and nothing about an
 * assignment can stop a visit from being recorded.
 */
// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------
export const ASSIGNMENT_STATUSES = ['ASSIGNED', 'ACCEPTED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];
/**
 * Who may move an assignment where. ASSIGNED -> ACCEPTED (agent, one tap) ->
 * IN_PROGRESS (agent starts the visit) -> COMPLETED (the visit reached the
 * server). CANCELLED by the manager from any open state. There is no decline
 * or postpone in this version.
 */
export const ASSIGNMENT_TRANSITIONS = {
    ASSIGNED: ['ACCEPTED', 'CANCELLED'],
    ACCEPTED: ['IN_PROGRESS', 'COMPLETED', 'CANCELLED'],
    IN_PROGRESS: ['COMPLETED', 'CANCELLED'],
    COMPLETED: [],
    CANCELLED: [],
};
export function canMoveAssignment(from, to) {
    return ASSIGNMENT_TRANSITIONS[from].includes(to);
}
/** Open = the manager may still reassign or cancel it; the agent may still act on it. */
export const OPEN_ASSIGNMENT_STATUSES = ['ASSIGNED', 'ACCEPTED', 'IN_PROGRESS'];
/** Not started = nobody has begun the visit yet (the manager's follow-up list at the end of the day). */
export const NOT_STARTED_STATUSES = ['ASSIGNED', 'ACCEPTED'];
export const ASSIGNMENT_PRIORITIES = ['NORMAL', 'HIGH', 'URGENT'];
/** Lower first: urgent, then high, then normal. */
export const PRIORITY_RANK = { URGENT: 0, HIGH: 1, NORMAL: 2 };
/** "ASG-000123": the human reference support sees on the Zoho ticket (through a mapping key). */
export const assignmentReference = (number) => `ASG-${String(number).padStart(6, '0')}`;
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
const optionKey = z.string().trim().min(1).max(100);
const isoDate = z.iso.date();
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time is HH:mm (24 h)');
/** One project of the plan, by option keys of the published form's project, visit type and service fields. */
export const plannedProjectSchema = z.object({
    projectKey: optionKey,
    visitTypeKey: optionKey.nullable().default(null),
    serviceKey: optionKey.nullable().default(null),
});
/**
 * Most branches one "assign" action may cover (one assignment each). Raised
 * from 50 so a whole area fits in one action (D83).
 */
export const MAX_BRANCHES_PER_ASSIGN = 200;
export const createAssignmentsRequestSchema = z.object({
    /** Several branches for one agent in one action is the normal case: one assignment per branch. */
    branchIds: z.array(z.uuid()).min(1).max(MAX_BRANCHES_PER_ASSIGN),
    agentId: z.uuid(),
    scheduledDate: isoDate,
    scheduledTime: hhmm.nullable().default(null),
    priority: z.enum(ASSIGNMENT_PRIORITIES).default('NORMAL'),
    projects: z
        .array(plannedProjectSchema)
        .max(10)
        .default([])
        .refine((list) => new Set(list.map((p) => p.projectKey)).size === list.length, 'A project is listed twice'),
    /** "المطلوب من الزيارة". */
    instruction: z.string().trim().max(1000).nullable().default(null),
});
/**
 * Assign by area, step one (D83): the area expanded into its active branches,
 * so the manager can untick some before confirming. Step two is the normal
 * create request with the branches that are left. Nothing is written here.
 */
export const expandAreaQuerySchema = z.object({ areaId: z.uuid(), agentId: z.uuid().optional(), date: isoDate.optional() });
export const expandAreaResponseSchema = z.object({
    area: z.object({ id: z.string(), nameAr: z.string(), nameEn: z.string().nullable(), zoneNameAr: z.string() }),
    branches: z.array(z.object({
        id: z.string(),
        name: z.string(),
        code: z.string().nullable(),
        brandName: z.string(),
        located: z.boolean(),
        /** Already assigned to this agent on this date (still open): unticked by default, never assigned twice by accident. */
        alreadyAssigned: z.boolean(),
    })),
    /** Active branches beyond MAX_BRANCHES_PER_ASSIGN that did not fit (the manager splits the area). */
    truncated: z.number().int(),
});
export const reassignRequestSchema = z.object({
    agentId: z.uuid(),
    /** Optional: move it to another day at the same time. */
    scheduledDate: isoDate.optional(),
    note: z.string().trim().max(500).nullable().default(null),
});
/**
 * Edit an assignment nobody has started: any of the plan's details, the agent
 * included. A field left out keeps its value; scheduledTime and instruction
 * take null to clear them.
 */
export const updateAssignmentRequestSchema = z.object({
    agentId: z.uuid().optional(),
    scheduledDate: isoDate.optional(),
    scheduledTime: hhmm.nullable().optional(),
    priority: z.enum(ASSIGNMENT_PRIORITIES).optional(),
    projects: z
        .array(plannedProjectSchema)
        .max(10)
        .refine((list) => new Set(list.map((p) => p.projectKey)).size === list.length, 'A project is listed twice')
        .optional(),
    instruction: z.string().trim().max(1000).nullable().optional(),
    note: z.string().trim().max(500).nullable().default(null),
});
export const cancelAssignmentRequestSchema = z.object({ reason: z.string().trim().min(3).max(500) });
/** Accept / start from the phone: the device time it happened (it may be sent later, from the offline queue). */
export const assignmentActionRequestSchema = z.object({ at: z.iso.datetime({ offset: true }) });
// ---------------------------------------------------------------------------
// Plan vs reality
// ---------------------------------------------------------------------------
export const DEVIATION_KINDS = ['BRANCH', 'PROJECT_ADDED', 'PROJECT_REMOVED', 'VISIT_TYPE', 'SERVICE'];
export const assignmentDeviationSchema = z.object({
    kind: z.enum(DEVIATION_KINDS),
    /** The project it is about (option key; null for the branch, or a project typed as "not in the list"). */
    projectKey: z.string().nullable(),
    /** What the plan said and what the visit did: option keys, or a branch id / typed name. */
    planned: z.string().nullable(),
    actual: z.string().nullable(),
});
/**
 * Where the visit differed from its plan. Computed by the server from what the
 * plan said and what was submitted, so it is the same whatever the phone did:
 * a changed branch, a planned project not visited, a project added, and a visit
 * type or service different from the one the manager expected (only where the
 * manager gave one).
 */
export function planDeviations(plan, visit) {
    const deviations = [];
    if (visit.branchId !== plan.branchId) {
        deviations.push({ kind: 'BRANCH', projectKey: null, planned: plan.branchId, actual: visit.branchId ?? visit.manualBranchName ?? null });
    }
    if (plan.projects.length === 0)
        return deviations;
    const done = [...visit.projects];
    for (const planned of plan.projects) {
        const index = done.findIndex((p) => p.projectKey === planned.projectKey);
        if (index === -1) {
            deviations.push({ kind: 'PROJECT_REMOVED', projectKey: planned.projectKey, planned: planned.projectKey, actual: null });
            continue;
        }
        const [actual] = done.splice(index, 1);
        if (planned.visitTypeKey && actual.visitTypeKey !== planned.visitTypeKey) {
            deviations.push({ kind: 'VISIT_TYPE', projectKey: planned.projectKey, planned: planned.visitTypeKey, actual: actual.visitTypeKey });
        }
        if (planned.serviceKey && actual.serviceKey !== planned.serviceKey) {
            deviations.push({ kind: 'SERVICE', projectKey: planned.projectKey, planned: planned.serviceKey, actual: actual.serviceKey });
        }
    }
    for (const extra of done) {
        deviations.push({ kind: 'PROJECT_ADDED', projectKey: extra.projectKey, planned: null, actual: extra.projectKey ?? extra.projectName });
    }
    return deviations;
}
// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------
export const assignmentProjectDtoSchema = z.object({
    project: optionRefSchema,
    visitType: optionRefSchema.nullable(),
    service: optionRefSchema.nullable(),
});
const person = z.object({ id: z.string(), fullName: z.string() });
export const assignmentDtoSchema = z.object({
    id: z.string(),
    reference: z.string(),
    status: z.enum(ASSIGNMENT_STATUSES),
    priority: z.enum(ASSIGNMENT_PRIORITIES),
    /** YYYY-MM-DD (the plan's day, in the app time zone). */
    scheduledDate: z.string(),
    scheduledTime: z.string().nullable(),
    agent: person,
    assignedBy: person,
    branch: z.object({
        id: z.string(),
        name: z.string(),
        code: z.string().nullable(),
        /** The area's Arabic name (old field kept for older apps). */
        area: z.string().nullable(),
        /** Zone -> Area -> Branch (D81). Optional so the phone accepts lists cached before the hierarchy. */
        areaId: z.string().nullable().optional(),
        areaNameEn: z.string().nullable().optional(),
        zoneName: z.string().nullable().optional(),
        brandId: z.string(),
        brandName: z.string(),
        /** Verified branches only, coarse (~110 m) like the mobile catalog (D14). */
        approxLatitude: z.number().nullable(),
        approxLongitude: z.number().nullable(),
    }),
    projects: z.array(assignmentProjectDtoSchema),
    instruction: z.string().nullable(),
    acceptedAt: z.string().nullable(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    /** The visit it produced (one-to-one), once received. */
    visit: z.object({ submissionId: z.string(), status: z.enum(VISIT_STATUSES), zohoTicketNumber: z.string().nullable() }).nullable(),
    deviations: z.array(assignmentDeviationSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createAssignmentsResponseSchema = z.object({ created: z.array(assignmentDtoSchema) });
/** The manager's numbers per agent per day. */
export const assignmentCountsSchema = z.object({
    assigned: z.number().int(),
    accepted: z.number().int(),
    inProgress: z.number().int(),
    completed: z.number().int(),
    notStarted: z.number().int(),
    cancelled: z.number().int(),
});
export function countAssignments(list) {
    const of = (status) => list.filter((a) => a.status === status).length;
    return {
        // Cancelled assignments are not "assigned" work any more.
        assigned: list.filter((a) => a.status !== 'CANCELLED').length,
        accepted: of('ACCEPTED'),
        inProgress: of('IN_PROGRESS'),
        completed: of('COMPLETED'),
        notStarted: list.filter((a) => NOT_STARTED_STATUSES.includes(a.status)).length,
        cancelled: of('CANCELLED'),
    };
}
export const assignmentPlanQuerySchema = z.object({ date: isoDate, ...geoFilterShape });
export const assignmentPlanResponseSchema = z.object({
    date: z.string(),
    /** Every agent the viewer may plan for (their team), with that day's assignments. */
    agents: z.array(person.extend({ counts: assignmentCountsSchema, assignments: z.array(assignmentDtoSchema) })),
    totals: assignmentCountsSchema,
});
export const teamMemberSchema = person.extend({ phoneNumber: z.string(), role: z.string() });
/** The agent's own list: open assignments from today on (and today's finished ones), ordered for the phone. */
export const myAssignmentsResponseSchema = z.object({
    items: z.array(assignmentDtoSchema),
    /** The server's "today" (app time zone), so the phone splits today / upcoming the same way. */
    today: z.string(),
    generatedAt: z.string(),
});
/** Today's first, then upcoming by day; within a day by priority, then scheduled time (none last). */
export function sortAssignmentsForAgent(list) {
    return [...list].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate) ||
        PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
        (a.scheduledTime ?? '99:99').localeCompare(b.scheduledTime ?? '99:99') ||
        a.createdAt.localeCompare(b.createdAt));
}
// ---------------------------------------------------------------------------
// Reporting
// ---------------------------------------------------------------------------
export const assignmentReportQuerySchema = z.object({ agentId: z.uuid().optional(), ...geoFilterShape }).and(dateRangeQuerySchema);
export const assignmentListQuerySchema = paginationQuerySchema
    .extend({
    agentId: z.uuid().optional(),
    status: z.enum(ASSIGNMENT_STATUSES).optional(),
    ...geoFilterShape,
    /** Only assignments whose visit differed from the plan. */
    deviatedOnly: queryBooleanSchema.optional(),
})
    .and(dateRangeQuerySchema);
export const assignmentReportResponseSchema = z.object({
    /** Per agent per day: assigned vs completed, and how long accept -> start and start -> completion took. */
    byAgentDay: z.array(z.object({
        date: z.string(),
        agent: person,
        assigned: z.number().int(),
        completed: z.number().int(),
        notStarted: z.number().int(),
        cancelled: z.number().int(),
        /** Averages in minutes over the assignments that have both times; null when none. */
        avgAcceptToStartMinutes: z.number().nullable(),
        avgStartToCompleteMinutes: z.number().nullable(),
    })),
    totals: assignmentCountsSchema.extend({
        deviated: z.number().int(),
        avgAcceptToStartMinutes: z.number().nullable(),
        avgStartToCompleteMinutes: z.number().nullable(),
    }),
    /** Deviations by kind over the range. */
    deviationsByKind: z.array(z.object({ kind: z.enum(DEVIATION_KINDS), count: z.number().int() })),
});
//# sourceMappingURL=assignments.js.map